<script setup lang="ts">
  import * as z from 'zod';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import type { TextInputProperties as TextInputPropsType } from '../../../model/form.types';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import { createSignKeyBlocker } from '../../../lib/blockSignKeys';
  import { textLengthRangeMessage } from '@record-fill';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
  } from '@/shared/ui';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  const blockSignKeys = createSignKeyBlocker(['-', '+']);

  type FormValues = {
    name: string;
    value?: string;
    placeholder?: string;
    label?: string;
    labelPosition?: 'top' | 'left';
    regex?: string;
    required?: boolean;
    hasMinLength?: boolean;
    hasMaxLength?: boolean;
    minLength?: number;
    maxLength?: number;
  };

  const { values } = usePropertyForm<FormValues, TextInputPropsType>({
    elementId: () => props.elementId,
    validationSchema: z.object({
      name: z.string().min(1, 'O nome é obrigatório').max(FORM_FIELD_CHARACTER_LIMITS.FIELD_ID),
      value: z.string().max(FORM_FIELD_CHARACTER_LIMITS.VALUE).optional(),
      placeholder: z.string().max(FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT).optional(),
      label: z.string().max(FORM_FIELD_CHARACTER_LIMITS.LABEL).optional(),
      labelPosition: z.enum(['top', 'left']).optional(),
      regex: z.string().optional(),
      required: z.boolean().optional(),
      hasMinLength: z.boolean().optional(),
      hasMaxLength: z.boolean().optional(),
      minLength: z.preprocess(
        (val) => (val === undefined || val === null || val === '' ? undefined : Number(val)),
        z
          .number({ message: 'Digite um valor' })
          .min(1, 'Mínimo de 1 caractere')
          .max(100, 'Máximo de 100 caracteres')
          .optional()
      ),
      maxLength: z.preprocess(
        (val) => (val === undefined || val === null || val === '' ? undefined : Number(val)),
        z
          .number({ message: 'Digite um valor' })
          .min(1, 'Mínimo de 1 caractere')
          .max(100, 'Máximo de 100 caracteres')
          .optional()
      ),
    }).superRefine((form, ctx) => {
      const minLength = form.hasMinLength ? form.minLength : undefined;
      const maxLength = form.hasMaxLength ? form.maxLength : undefined;
      if (minLength !== undefined && maxLength !== undefined && minLength > maxLength) {
        ctx.addIssue({
          code: 'custom',
          path: ['maxLength'],
          message: 'O número mínimo de caracteres não pode ser maior que o número máximo.',
        });
        return;
      }
      const length = form.value?.length ?? 0;
      if (length === 0) return;
      if ((minLength !== undefined && length < minLength) || (maxLength !== undefined && length > maxLength)) {
        ctx.addIssue({ code: 'custom', path: ['value'], message: textLengthRangeMessage(minLength, maxLength) });
      }
    }),
    extractInitialValues: (properties: TextInputPropsType | undefined): FormValues => ({
      name: properties?.name ?? '',
      value: properties?.value ?? '',
      placeholder: properties?.placeholder ?? '',
      label: properties?.label ?? '',
      labelPosition: properties?.labelPosition ?? 'top',
      regex: properties?.regex ?? '',
      required: properties?.required ?? false,
      hasMinLength: (properties?.minLength ?? 0) > 0,
      hasMaxLength: (properties?.maxLength ?? 0) > 0,
      minLength: properties?.minLength ?? undefined,
      maxLength: properties?.maxLength ?? undefined,
    }),
    mapValuesToProperties: (values: FormValues, currentProperties: TextInputPropsType) => ({
      properties: {
        ...currentProperties,
        name: values.name || currentProperties.name,
        value: values.value || undefined,
        placeholder: values.placeholder || undefined,
        label: values.label || undefined,
        labelPosition: values.labelPosition,
        required: values.required ?? false,
        regex: values.regex || undefined,
        minLength: values.hasMinLength ? (values.minLength ?? undefined) : undefined,
        maxLength: values.hasMaxLength ? (values.maxLength ?? undefined) : undefined,
      },
    }),
  });
</script>

<template>
  <div class="flex flex-col">
    <!-- ID do campo -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="name"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            ID do campo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="nome_do_campo"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIELD_ID"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Valor -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="value"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Valor
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o valor"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.VALUE"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Texto auxiliar -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="placeholder"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Texto auxiliar
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o texto auxiliar"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Rótulo + Posicionamento -->
    <div class="border-b border-gray-02 py-4 flex flex-col gap-[10px]">
      <FormField
        v-slot="{ componentField }"
        name="label"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Rótulo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui"
              class="h-[42px]"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.LABEL"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="labelPosition"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Posicionamento
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione a posição" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="top">
                Acima do campo
              </SelectItem>
              <SelectItem value="left">
                À Esquerda do Campo
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Expressão Regular -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="regex"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Expressão Regular
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o Regex"
              class="h-11"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Obrigatória -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="required"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Obrigatória
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
    </div>

    <!-- Número mínimo de caracteres -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="hasMinLength"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Número mínimo de caracteres
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
      <div
        v-if="values.hasMinLength"
        class="mt-3"
      >
        <FormField
          v-slot="{ componentField }"
          name="minLength"
        >
          <FormItem class="flex flex-col gap-2">
            <FormControl>
              <Input
                v-bind="componentField"
                type="number"
                min="1"
                max="100"
                placeholder="Ex: 1"
                class="h-11 w-28 border-gray-02 text-gray-07 [&::-webkit-inner-spin-button]:opacity-100 [&::-webkit-outer-spin-button]:opacity-100"
                @keydown="blockSignKeys"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>
    </div>

    <!-- Número máximo de caracteres -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="hasMaxLength"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Número máximo de caracteres
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
      <div
        v-if="values.hasMaxLength"
        class="mt-3"
      >
        <FormField
          v-slot="{ componentField }"
          name="maxLength"
        >
          <FormItem class="flex flex-col gap-2">
            <FormControl>
              <Input
                v-bind="componentField"
                type="number"
                min="1"
                max="100"
                placeholder="Ex: 100"
                class="h-11 w-28 border-gray-02 text-gray-07 [&::-webkit-inner-spin-button]:opacity-100 [&::-webkit-outer-spin-button]:opacity-100"
                @keydown="blockSignKeys"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>
    </div>
  </div>
</template>
